<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import NetworkButton from '@/pages/MapPage/NetworkButton.vue';
import { AppRoute } from '@/libs/routing';
import { closeSearch } from '@/pages/SearchPage/close-search';
import { useSearchStore } from '@/stores/search';

/**
 * The home search bar. One and the same field on the home and in the search:
 * tapping it opens the search route, which slides the results panel under it
 * while the field itself only switches to its active state in place.
 */
const props = defineProps<{
  /** The search route is open: the field is live and the ✕ replaces the settings button */
  active: boolean;
}>();

const router = useRouter();
const searchStore = useSearchStore();

const searchBar = ref<InstanceType<typeof SearchBar>>();

// The field is the only interactive element: a tap on its wrapper focuses it, and
// the focus (tap or keyboard) opens the search. Focus inside the tap itself: the
// keyboard only opens from a user gesture.
function open() {
  if (!props.active) router.push({ name: AppRoute.SEARCH });
}

function close() {
  closeSearch(router);
}

function goToSettings() {
  router.push({ name: AppRoute.SETTINGS });
}

// Covers every way out of the search (✕, Android back, browser back) and in
// (deep link): the field follows the route, not only the taps on the bar.
function syncField(active: boolean) {
  if (active) searchBar.value?.focus();
  else {
    searchBar.value?.blur();
    // Whitespace-only text never reached the store: clear the field itself too.
    searchBar.value?.clear();
    searchStore.reset();
  }
}

// From `onMounted`, not an immediate watcher: that one runs during setup, before
// the template ref exists, so a deep link never got its focus.
onMounted(() => syncField(props.active));
watch(() => props.active, syncField);

onBeforeUnmount(() => searchStore.reset());
</script>

<template>
  <div class="search-top-bar" :class="{ 'search-top-bar--active': active }">
    <NetworkButton v-if="!active" />

    <div class="search-top-bar__field" @click="active || searchBar?.focus()">
      <SearchBar ref="searchBar" v-model="searchStore.query" @focus="open()" />
    </div>

    <div class="search-top-bar__action">
      <transition name="fade">
        <RoundedButton
          v-if="active"
          :aria-label="$t('common.back')"
          class="search-top-bar__close"
          icon="x"
          type="ghost"
          @click="close"
        />
        <button
          v-else
          :aria-label="$t('settings.title')"
          class="search-top-bar__settings"
          @click="goToSettings"
        >
          <BaseIcon name="cog" />
        </button>
      </transition>
    </div>
  </div>
</template>

<style lang="scss">
$search-top-bar-easing: cubic-bezier(0.32, 0.72, 0, 1);

.search-top-bar {
  align-items: center;
  display: flex;
  gap: 10px;
  left: 0;
  // Same gutter in both states; the active state only swaps the 52px settings
  // button for the 40px close button and tightens the gap. The top padding plus
  // the 52px field is `--search-top-bar-height` (global.scss): keep them in step.
  padding: calc(12px + var(--status-bar-band)) $spacing-4 10px;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 3;

  &--active {
    gap: $spacing-2;
    padding: calc(12px + var(--status-bar-band)) $spacing-4 12px;
  }

  &__field {
    flex: 1;
    min-width: 0;
    pointer-events: auto;
  }

  .searchbar {
    // Floats over the map like the settings button; the shared field's soft
    // shadow is meant for a white page.
    margin-bottom: 0;
    padding-block: 13px;
  }

  // On the home taps land on the wrapper, which focuses the input: the focus
  // opens the search.
  &:not(&--active) .searchbar {
    pointer-events: none;
  }

  &--active .searchbar {
    &:focus-within,
    &.searchbar--active {
      box-shadow: inset 0 0 0 1px $color-neutral-highest;
    }
  }

  &__action {
    flex-shrink: 0;
    height: 52px;

    pointer-events: auto;
    position: relative;
    transition:
      width 0.3s $search-top-bar-easing,
      height 0.3s $search-top-bar-easing;
    width: 52px;
  }

  &--active &__action {
    height: 40px;
    width: 40px;
  }

  &__settings,
  &__close {
    inset: 0;
    position: absolute;
  }

  &__settings {
    align-items: center;
    background-color: $color-white;
    border: 1px solid $color-border-light;
    border-radius: 50%;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
    color: $color-neutral-highest;
    cursor: pointer;
    display: flex;
    justify-content: center;
    padding: 0;

    .base-icon {
      font-size: 26px;
    }
  }

  // The mockup draws the 24px glyph in a 40px hit area; the medium button uses 20px.
  &__close {
    height: 100%;
    width: 100%;

    .rounded-button__icon {
      font-size: 24px;
    }
  }
}
</style>
